import * as React from 'react';
import { AlertTriangle, Eye, MessageSquare, Plus, X } from 'lucide-react';
import { AutosaveField, WorkspaceButton, workspaceFieldClass } from '@/components/ui/workspace';
import { cn } from '@/lib/utils/cn';
import {
  nameTakenFromError,
  type ExistingItem,
  type ItemChange,
  type ListQuestion,
} from '@/lib/api/discovery-frameworks';
import { messageOf } from './framework-logic';

/**
 * Small building blocks of the Discovery frameworks screen (T-0184), each
 * mirroring a class of `docs/discovery/prototype/discovery.css`.
 */

export const LINK_BUTTON =
  'inline-flex items-center gap-1 rounded-chip text-meta font-medium text-ink-700 underline underline-offset-[3px] hover:text-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600';

/** A code in the mono face (`.mono-no`): D5, T3, Q-B, Q-B.7. Codes show here (Decision 54.3). */
export function CodeTag({ code, large = false }: { code: string; large?: boolean }) {
  return (
    <span
      className={cn(
        'flex-none whitespace-nowrap font-mono font-medium',
        large ? 'text-body-sm text-ink-900' : 'text-meta text-ink-700',
      )}
    >
      {code}
    </span>
  );
}

/** "New in draft" and the other draft markers (`.chip.chip-amber.sm`). */
export function ChangeTag({ change }: { change: ItemChange }) {
  if (!change) return null;
  const retired = change === 'retired';
  return (
    <span
      className={cn(
        'inline-flex h-5 flex-none items-center rounded-full px-2 align-middle text-[11px] font-medium',
        retired ? 'bg-ink-100 text-ink-500' : 'bg-accent-amber-100 text-accent-amber-700',
      )}
    >
      {change === 'added' ? 'New in draft' : retired ? 'Retired in draft' : 'Changed in draft'}
    </span>
  );
}

export function RetiredTag() {
  return (
    <span className="inline-flex h-5 flex-none items-center rounded-full bg-ink-100 px-2 text-[11px] font-medium text-ink-500">
      Retired
    </span>
  );
}

/** The dashed "+ Question" chip (`.dchip.add`). */
export function AddChip({
  children,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { children: React.ReactNode }) {
  return (
    <button
      type="button"
      className="inline-flex h-[22px] items-center gap-1 rounded-full border border-dashed border-ink-200 bg-paper-0 px-2 text-[11px] font-medium text-ink-500 hover:border-ink-300 hover:text-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600 disabled:opacity-50"
      {...props}
    >
      <Plus className="h-2.5 w-2.5" aria-hidden="true" />
      {children}
    </button>
  );
}

/** A pill that is on or off (`.fchip`), for picking several areas. */
export function ToggleChip({
  on,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { on: boolean }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      className={cn(
        'inline-flex h-7 items-center rounded-full border px-2.5 text-meta font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600 disabled:opacity-50',
        on
          ? 'border-ink-900 bg-ink-900 text-paper-0'
          : 'border-ink-200 bg-paper-0 text-ink-700 hover:border-ink-300',
      )}
      {...props}
    />
  );
}

/** A small yes/no flag (`.demo-toggle`), read-only outside edit mode. */
export function FlagToggle({
  on,
  readOnly,
  icon,
  children,
  onToggle,
}: {
  on: boolean;
  readOnly: boolean;
  icon?: React.ReactNode;
  children: React.ReactNode;
  onToggle?: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={on}
      aria-disabled={readOnly || undefined}
      onClick={readOnly ? undefined : onToggle}
      className={cn(
        'inline-flex h-[22px] items-center gap-1 whitespace-nowrap rounded-full border px-2 text-[11px] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
        on
          ? 'border-transparent bg-posture-healthy-tint text-posture-healthy'
          : 'border-ink-200 text-ink-500',
        readOnly ? 'cursor-default' : 'cursor-pointer',
      )}
    >
      {icon}
      {children}
    </button>
  );
}

/** The amber caution box (`.why`), for example "Corroboration is turned off". */
export function CautionNote({ children }: { children: React.ReactNode }) {
  return (
    <div
      role="note"
      className="flex gap-2 rounded-card bg-posture-at-risk-tint px-3 py-2.5 text-meta text-ink-700"
    >
      <AlertTriangle
        className="mt-0.5 h-3.5 w-3.5 flex-none text-posture-at-risk"
        aria-hidden="true"
      />
      <span>{children}</span>
    </div>
  );
}

export function FieldHelp({ children }: { children: React.ReactNode }) {
  return <p className="mt-1.5 text-meta text-ink-500">{children}</p>;
}

/** Panel footer text while editing: the draft saves itself. */
export function SaveStatus({ busy }: { busy: boolean }) {
  return (
    <span className="text-meta text-ink-500" aria-live="polite">
      {busy ? 'Saving…' : 'Saved to the draft as you type.'}
    </span>
  );
}

/** A selectable card (`.type-card`) for interview types and questionnaires. */
export function ItemCard({
  code,
  on,
  retired,
  label,
  aside,
  children,
  onClick,
}: {
  code: string;
  on: boolean;
  retired: boolean;
  label: string;
  aside: React.ReactNode;
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={on}
      aria-label={label}
      onClick={onClick}
      className={cn(
        'flex w-full items-start gap-4 rounded-card border bg-paper-0 px-4 py-3.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
        on
          ? 'border-accent-amber-600 shadow-[inset_3px_0_0_var(--accent-amber-600)]'
          : 'border-ink-100 hover:border-ink-300',
        retired && 'opacity-60',
      )}
    >
      <CodeTag code={code} large />
      <div className="min-w-0 flex-1">{children}</div>
      <div className="flex-none text-right text-meta leading-5 text-ink-500">{aside}</div>
    </button>
  );
}

/**
 * "+ Add …" that opens a field in place. The field is sent once, with
 * Enter or Add; `onAdd` resolves to a refusal to show, or null when added.
 */
export function InlineAdd({
  trigger,
  label,
  inputLabel,
  placeholder,
  busy,
  multiline = false,
  onAdd,
}: {
  trigger: 'row' | 'button' | 'chip';
  label: string;
  inputLabel: string;
  placeholder: string;
  busy: boolean;
  multiline?: boolean;
  onAdd: (text: string) => Promise<React.ReactNode | null>;
}) {
  const [open, setOpen] = React.useState(false);
  const [text, setText] = React.useState('');
  const [error, setError] = React.useState<React.ReactNode | null>(null);
  const [saving, setSaving] = React.useState(false);

  const close = () => {
    setOpen(false);
    setText('');
    setError(null);
  };
  const submit = async () => {
    const value = text.trim();
    if (!value || saving) return;
    setSaving(true);
    const refusal = await onAdd(value);
    setSaving(false);
    if (refusal == null) close();
    else setError(refusal);
  };

  if (!open) {
    if (trigger === 'chip') {
      return (
        <AddChip disabled={busy} onClick={() => setOpen(true)}>
          {label}
        </AddChip>
      );
    }
    if (trigger === 'button') {
      return (
        <WorkspaceButton size="md" className="self-start" onClick={() => setOpen(true)}>
          <Plus className="h-3.5 w-3.5" aria-hidden="true" />
          {label}
        </WorkspaceButton>
      );
    }
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-chip px-1.5 py-1 text-meta font-medium text-ink-500 hover:bg-ink-100 hover:text-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
      >
        <Plus className="h-3.5 w-3.5" aria-hidden="true" />
        {label}
      </button>
    );
  }

  const fieldProps = {
    'aria-label': inputLabel,
    value: text,
    placeholder,
    autoFocus: true,
    maxLength: 2000,
    autoComplete: 'off',
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setText(e.target.value);
      setError(null);
    },
    onKeyDown: (e: React.KeyboardEvent) => {
      e.stopPropagation();
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        void submit();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        close();
      }
    },
  };
  return (
    <div className="w-full py-1">
      <div className="flex items-start gap-1.5">
        {multiline ? (
          <textarea
            {...fieldProps}
            rows={2}
            className={cn(workspaceFieldClass, 'resize-y py-2 leading-5')}
          />
        ) : (
          <input {...fieldProps} type="text" className={cn(workspaceFieldClass, 'h-9')} />
        )}
        <WorkspaceButton
          size="md"
          variant="secondary"
          disabled={busy || saving || !text.trim()}
          onClick={() => void submit()}
        >
          {saving ? 'Adding…' : 'Add'}
        </WorkspaceButton>
        <WorkspaceButton size="md" variant="tertiary" onClick={close}>
          Cancel
        </WorkspaceButton>
      </div>
      {error ? (
        <p role="alert" className="mt-1.5 text-meta text-posture-critical">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/**
 * A refusal of an add, in words, with a way to the item that already has
 * the name: restore it when it is retired, or open it (rule 1).
 */
export function refusalOf(
  err: unknown,
  actions: { open: (item: ExistingItem) => void; restore: (item: ExistingItem) => void },
): React.ReactNode {
  const message = messageOf(err, 'It could not be added.');
  const taken = nameTakenFromError(err);
  const existing = taken?.existing;
  if (!existing) return message;
  const ref = existing.code ?? existing.name;
  return (
    <>
      {message}{' '}
      <button
        type="button"
        className={LINK_BUTTON}
        onClick={() => (taken.restorable ? actions.restore(existing) : actions.open(existing))}
      >
        {taken.restorable ? `Restore ${ref}` : `Open ${ref}`}
      </button>
    </>
  );
}

/**
 * The questions of an area, or an interview type's questions or on-screen
 * asks (`.probe` rows). Every question shows its code (Decision 54.3). In
 * edit mode the wording saves as you type, × retires (rule 3) and a
 * retired question can be restored.
 */
export function QuestionList({
  items,
  editing,
  busy,
  kind,
  addLabel,
  onSave,
  onStatus,
  onAdd,
}: {
  items: readonly ListQuestion[];
  editing: boolean;
  busy: boolean;
  kind: 'question' | 'on_screen';
  addLabel: string;
  onSave: (q: ListQuestion, text: string) => void;
  onStatus: (q: ListQuestion, status: 'active' | 'retired') => void;
  onAdd: (text: string) => Promise<React.ReactNode | null>;
}) {
  const Icon = kind === 'on_screen' ? Eye : MessageSquare;
  if (!editing) {
    const active = items.filter((q) => q.status === 'active');
    if (active.length === 0) return <p className="text-meta text-ink-500">None</p>;
    return (
      <ul>
        {active.map((q) => (
          <li
            key={q.id}
            className="flex items-start gap-2.5 border-b border-ink-100 py-[7px] text-body-sm text-ink-900 last:border-b-0"
          >
            <Icon className="mt-[3px] h-3.5 w-3.5 flex-none text-ink-500" aria-hidden="true" />
            <CodeTag code={q.code} />
            <span className="min-w-0">{q.text}</span>
          </li>
        ))}
      </ul>
    );
  }
  return (
    <div className="flex flex-col gap-1.5">
      {items.map((q) =>
        q.status === 'retired' ? (
          <div key={q.id} className="flex items-center gap-2 py-1 text-body-sm">
            <CodeTag code={q.code} />
            <span className="min-w-0 flex-1 truncate text-ink-300 line-through">{q.text}</span>
            <WorkspaceButton
              variant="tertiary"
              disabled={busy}
              onClick={() => onStatus(q, 'active')}
            >
              Restore {q.code}
            </WorkspaceButton>
          </div>
        ) : (
          <div key={q.id} className="flex items-start gap-1.5">
            <span className="w-14 flex-none pt-2">
              <CodeTag code={q.code} />
            </span>
            <AutosaveField
              multiline
              required
              aria-label={`Wording of ${q.code}`}
              value={q.text}
              onSave={(text) => onSave(q, text)}
            />
            <button
              type="button"
              aria-label={`Retire ${q.code}`}
              title="Retire"
              disabled={busy}
              onClick={() => onStatus(q, 'retired')}
              className="mt-1.5 inline-flex h-[26px] w-[26px] flex-none items-center justify-center rounded-chip text-ink-500 hover:bg-ink-100 hover:text-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
            >
              <X className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </div>
        ),
      )}
      <div>
        <InlineAdd
          trigger="chip"
          label={addLabel}
          inputLabel={`New ${addLabel.toLowerCase()}`}
          placeholder={kind === 'on_screen' ? 'What to ask to see on screen' : 'The question'}
          multiline
          busy={busy}
          onAdd={onAdd}
        />
      </div>
    </div>
  );
}
